/**
 * Utility functions and data processors for TruckTrack Fleet & Job Reports
 */

/**
 * Format currency in Australian Dollars (AUD)
 */
export function formatCurrency(amount) {
    if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
}

/**
 * Format tonnage values (e.g. 1,500 t)
 */
export function formatTonnes(tonnes, decimals = 1) {
    if (tonnes === null || tonnes === undefined || isNaN(tonnes)) return '0 t';
    const num = Number(tonnes);
    return `${num.toLocaleString('en-AU', { maximumFractionDigits: decimals })} t`;
}

/**
 * Format duration in minutes into a human-readable string (e.g. "45 min", "2h 15m")
 */
export function formatDuration(totalMinutes) {
    if (!totalMinutes || isNaN(totalMinutes) || totalMinutes <= 0) return '0 min';
    const hrs = Math.floor(totalMinutes / 60);
    const mins = Math.round(totalMinutes % 60);
    if (hrs > 0) {
        return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
    }
    return `${mins} min`;
}

/**
 * Format date-time string (e.g. "07/10 11:12" or "11:12:32")
 */
export function formatDateTime(dtStr, timeOnly = false) {
    if (!dtStr) return '-';
    try {
        const clean = dtStr.replace(' ', 'T');
        const d = new Date(clean);
        if (isNaN(d.getTime())) return String(dtStr);
        if (timeOnly) {
            return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
        }
        return d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit' }) + ' ' +
               d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch (e) {
        return String(dtStr);
    }
}

/**
 * Format date nicely (e.g. "Wednesday, 07 October 2026")
 */
export function formatDateLong(dateStr) {
    if (!dateStr) return '';
    try {
        const parts = String(dateStr).slice(0, 10).split('-');
        if (parts.length === 3) {
            const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
            return d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        }
        return String(dateStr);
    } catch (e) {
        return String(dateStr);
    }
}

/**
 * Export table data to standard RFC-4180 CSV
 */
export function exportToCSV(filename, columns, rows) {
    const escapeCell = (val) => {
        if (val === null || val === undefined) return '""';
        const str = String(val).replace(/"/g, '""');
        return `"${str}"`;
    };

    const headerRow = columns.map(c => escapeCell(c.label || c.name || c.key || c)).join(',');
    const bodyRows = rows.map(row => {
        return columns.map(c => {
            const key = c.key || c.id || c;
            let val;
            if (typeof c.formatter === 'function') {
                val = c.formatter(row);
            } else {
                val = row[key];
            }
            return escapeCell(val);
        }).join(',');
    });

    const csvContent = '\uFEFF' + [headerRow, ...bodyRows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const cleanFilename = (filename || 'trucktrack-report').replace(/\.csv$/i, '') + '.csv';
    link.setAttribute('download', cleanFilename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Normalizes customer name from job
 */
export function getCustomerName(job) {
    if (!job) return 'Unknown Customer';
    if (job.customer && typeof job.customer === 'object') {
        return job.customer.name || job.customer.company_name || 'Customer #' + (job.customer.id || '');
    }
    return job.customer || 'Customer #' + (job.id || '');
}

/**
 * Normalizes truck rego / ID
 */
export function getTruckRego(truckObj) {
    if (!truckObj) return 'Unknown Truck';
    if (truckObj.truck && typeof truckObj.truck === 'object' && truckObj.truck.id) {
        return truckObj.truck.id;
    }
    if (typeof truckObj.truck === 'string' && truckObj.truck) {
        return truckObj.truck;
    }
    if (truckObj.truck_id) return truckObj.truck_id;
    if (truckObj.id) {
        const parts = String(truckObj.id).split('-');
        return parts[parts.length - 1];
    }
    return 'TRK-' + (truckObj.dispatch_order || '1');
}

/**
 * Normalizes driver name
 */
export function getDriverName(truckObj) {
    if (!truckObj) return 'Assigned Driver';
    if (truckObj.driver && typeof truckObj.driver === 'object') {
        return truckObj.driver.name || 'Assigned Driver';
    }
    if (typeof truckObj.driver === 'string') return truckObj.driver;
    return 'Assigned Driver';
}

/**
 * Computes payload capacity in tonnes for an allocated truck
 */
export function getTruckCapacity(truckObj) {
    if (!truckObj) return 10;
    if (truckObj.total_capacity && !isNaN(truckObj.total_capacity) && truckObj.total_capacity > 0) {
        return Number(truckObj.total_capacity);
    }
    let truckCap = (truckObj.truck && typeof truckObj.truck === 'object' && truckObj.truck.capacity)
        ? Number(truckObj.truck.capacity) : 10;
    let trailerCap = (truckObj.trailer && typeof truckObj.trailer === 'object' && truckObj.trailer.capacity)
        ? Number(truckObj.trailer.capacity) : 0;
    return truckCap + trailerCap;
}

/**
 * Resolves pickup address string
 */
export function getPickupAddress(material, job) {
    const addr = material?.load_address || job?.address;
    if (!addr) return 'Site Pickup';
    if (typeof addr === 'string') return addr;
    return addr.suburb || addr.name || addr.street || 'Site Pickup';
}

/**
 * Resolves disposal address string
 */
export function getDisposalAddress(material) {
    const addr = material?.disposal_address || material?.yard?.address;
    if (!addr) return 'Disposal Facility';
    if (typeof addr === 'string') return addr;
    return addr.suburb || addr.name || addr.street || 'Disposal Facility';
}

/**
 * Scans all loads across all jobs and returns sorted list of available operational dates
 */
export function extractAvailableDates(jobs) {
    const dateCounts = new Map();
    if (!Array.isArray(jobs)) return [];

    const todayStr = new Date().toISOString().slice(0, 10);

    jobs.forEach(job => {
        (job.materials || []).forEach(m => {
            (m.allocated_trucks || []).forEach(t => {
                (t.loads || []).forEach(l => {
                    if (l.load_time) {
                        const dStr = l.load_time.slice(0, 10);
                        dateCounts.set(dStr, (dateCounts.get(dStr) || 0) + 1);
                    }
                });
            });
        });
    });

    const dates = Array.from(dateCounts.keys()).sort((a, b) => b.localeCompare(a));
    return dates.map(d => {
        const isToday = d === todayStr;
        let label = d;
        try {
            const parts = d.split('-');
            const dt = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
            label = dt.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
            if (isToday) label += ' (Today)';
        } catch (e) {
            // fallback
        }
        return {
            date: d,
            label,
            loadsCount: dateCounts.get(d)
        };
    });
}

/**
 * Filters and compiles load records & truck summaries for either "by date" or "by entire job"
 */
export function extractJobLoads({ jobs = [], reportMode = 'job', selectedDate = '', selectedJobId = 'all', selectedTruckId = 'all' }) {
    const allMatchingLoads = [];
    const truckSummaryMap = new Map();

    if (!Array.isArray(jobs)) {
        return { loads: [], truckSummaries: [], kpis: getEmptyKPIs() };
    }

    // Step 1: Filter jobs
    const targetJobs = jobs.filter(j => {
        if (selectedJobId !== 'all') {
            return String(j.id) === String(selectedJobId) || String(j.job_number) === String(selectedJobId);
        }
        return true;
    });

    // Step 2: Iterate and extract loads
    targetJobs.forEach(job => {
        const customerName = getCustomerName(job);
        const jobNumber = job.job_number || `JOB-${job.id}`;
        const jobStatus = (job.status || 'active').toLowerCase();

        (job.materials || []).forEach(m => {
            const materialName = m.name || 'General Material';
            const pickupSite = getPickupAddress(m, job);
            const disposalSite = getDisposalAddress(m);

            (m.allocated_trucks || []).forEach(t => {
                const rego = getTruckRego(t);
                if (selectedTruckId !== 'all' && rego.toLowerCase() !== selectedTruckId.toLowerCase()) {
                    return;
                }

                const driver = getDriverName(t);
                const cap = getTruckCapacity(t);
                const rawLoads = Array.isArray(t.loads) ? t.loads : [];

                // Filter loads by date if reportMode is 'date'
                const filteredLoads = rawLoads.filter(l => {
                    if (reportMode === 'date' && selectedDate) {
                        return l.load_time && l.load_time.startsWith(selectedDate);
                    }
                    return true;
                });

                if (filteredLoads.length === 0) return;

                // Truck summary key
                const tKey = `${rego}-${job.id}`;
                if (!truckSummaryMap.has(tKey)) {
                    truckSummaryMap.set(tKey, {
                        key: tKey,
                        rego,
                        driver,
                        capacity: cap,
                        jobId: job.id,
                        jobNumber,
                        jobStatus,
                        customerName,
                        materialName,
                        pickupSite,
                        disposalSite,
                        loadsCount: 0,
                        totalTonnes: 0,
                        tripDurations: [],
                        cycleDurations: [],
                        firstLoadTime: null,
                        lastLoadTime: null
                    });
                }
                const summary = truckSummaryMap.get(tKey);

                // Sort truck loads chronologically
                const sortedLoads = [...filteredLoads].sort((a, b) => {
                    const tA = new Date((a.load_time || '').replace(' ', 'T')).getTime() || 0;
                    const tB = new Date((b.load_time || '').replace(' ', 'T')).getTime() || 0;
                    return tA - tB;
                });

                sortedLoads.forEach((load, idx) => {
                    summary.loadsCount++;
                    summary.totalTonnes += cap;

                    let loadStart = load.load_time ? new Date(load.load_time.replace(' ', 'T')) : null;
                    let unloadEnd = load.unload_time ? new Date(load.unload_time.replace(' ', 'T')) : null;

                    let tripMins = null;
                    if (loadStart && unloadEnd && !isNaN(loadStart) && !isNaN(unloadEnd)) {
                        const diff = Math.max(0, Math.round((unloadEnd - loadStart) / 60000));
                        if (diff < 400) {
                            tripMins = diff;
                            summary.tripDurations.push(diff);
                        }
                    }

                    let cycleMins = null;
                    if (idx > 0) {
                        const prevLoadStart = sortedLoads[idx - 1].load_time
                            ? new Date(sortedLoads[idx - 1].load_time.replace(' ', 'T')) : null;
                        if (prevLoadStart && loadStart && !isNaN(prevLoadStart) && !isNaN(loadStart)) {
                            const diffCycle = Math.max(0, Math.round((loadStart - prevLoadStart) / 60000));
                            if (diffCycle > 0 && diffCycle < 600) {
                                cycleMins = diffCycle;
                                summary.cycleDurations.push(diffCycle);
                            }
                        }
                    }

                    if (load.load_time) {
                        if (!summary.firstLoadTime) summary.firstLoadTime = load.load_time;
                        summary.lastLoadTime = load.load_time;
                    }
                    if (load.unload_time) {
                        summary.lastLoadTime = load.unload_time;
                    }

                    const docket = load.docket ||
                        `DOC-${job.id || '422'}-${rego.replace(/\D/g, '') || '1'}-${String(idx + 1).padStart(3, '0')}`;

                    allMatchingLoads.push({
                        loadId: `${rego}-${idx + 1}`,
                        loadIndex: idx + 1,
                        rego,
                        driver,
                        capacity: cap,
                        jobId: job.id,
                        jobNumber,
                        jobStatus,
                        customerName,
                        materialName,
                        loadTime: load.load_time,
                        unloadTime: load.unload_time,
                        tripMins,
                        cycleMins,
                        docket,
                        issues: load.issues || 'Verified & Tipped'
                    });
                });
            });
        });
    });

    // Sort all loads chronologically
    allMatchingLoads.sort((a, b) => {
        const tA = new Date((a.loadTime || '').replace(' ', 'T')).getTime() || 0;
        const tB = new Date((b.loadTime || '').replace(' ', 'T')).getTime() || 0;
        return tA - tB;
    });

    // Re-index sequential order across entire selection
    allMatchingLoads.forEach((l, i) => l.sequenceIndex = i + 1);

    // Compute truck summaries averages
    const truckSummaries = Array.from(truckSummaryMap.values()).map(s => {
        const avgTrip = s.tripDurations.length > 0
            ? Math.round(s.tripDurations.reduce((a, b) => a + b, 0) / s.tripDurations.length)
            : 10;
        const avgCycle = s.cycleDurations.length > 0
            ? Math.round(s.cycleDurations.reduce((a, b) => a + b, 0) / s.cycleDurations.length)
            : (avgTrip * 2 + 8);

        return {
            ...s,
            avgTripMins: avgTrip,
            avgCycleMins: avgCycle
        };
    }).sort((a, b) => b.loadsCount - a.loadsCount);

    // Compute KPIs
    const totalLoads = allMatchingLoads.length;
    const totalTonnes = truckSummaries.reduce((sum, s) => sum + s.totalTonnes, 0);
    const activeTrucksCount = truckSummaries.length;
    const activeDriversCount = new Set(truckSummaries.map(s => s.driver)).size;
    const avgCycleMins = truckSummaries.length > 0
        ? Math.round(truckSummaries.reduce((sum, s) => sum + s.avgCycleMins, 0) / truckSummaries.length)
        : 0;
    const avgTripMins = truckSummaries.length > 0
        ? Math.round(truckSummaries.reduce((sum, s) => sum + s.avgTripMins, 0) / truckSummaries.length)
        : 0;

    let firstLoadTime = null;
    let lastLoadTime = null;
    if (allMatchingLoads.length > 0) {
        firstLoadTime = allMatchingLoads[0].loadTime;
        lastLoadTime = allMatchingLoads[allMatchingLoads.length - 1].unloadTime || allMatchingLoads[allMatchingLoads.length - 1].loadTime;
    }

    let spanFormatted = 'Shift In Progress';
    if (firstLoadTime && lastLoadTime) {
        const t1 = new Date(firstLoadTime.replace(' ', 'T')).getTime();
        const t2 = new Date(lastLoadTime.replace(' ', 'T')).getTime();
        if (!isNaN(t1) && !isNaN(t2) && t2 > t1) {
            const spanHrs = Math.floor((t2 - t1) / 3600000);
            const spanMins = Math.round(((t2 - t1) % 3600000) / 60000);
            spanFormatted = `${spanHrs > 0 ? spanHrs + 'h ' : ''}${spanMins}m span`;
        }
    }

    return {
        loads: allMatchingLoads,
        truckSummaries,
        kpis: {
            totalLoads,
            totalTonnes,
            activeTrucksCount,
            activeDriversCount,
            avgCycleMins,
            avgTripMins,
            firstLoadTime,
            lastLoadTime,
            spanFormatted
        }
    };
}

function getEmptyKPIs() {
    return {
        totalLoads: 0,
        totalTonnes: 0,
        activeTrucksCount: 0,
        activeDriversCount: 0,
        avgCycleMins: 0,
        avgTripMins: 0,
        firstLoadTime: null,
        lastLoadTime: null,
        spanFormatted: '-'
    };
}
